import type { MachineSummary } from "@racler/protocol";

/** Recently opened Projects, remembered per browser. Used for the Home "Continue" and "Recent projects" lists. */
export interface RecentProject {
  machineId: string;
  projectId: string;
  name: string;
  openedAt: number;
  workspaceName?: string;
  path?: string;
}

const KEY = "racler.recentProjects.v1";
/** Pre-M2C.1 key. Its entries are valid Project entries (the migration kept every id), so they are carried over. */
const LEGACY_KEY = "racler.recentWorkspaces.v1";
export const MAX_RECENT = 50;
const UUID = /^[0-9a-f-]{36}$/i;
const ID = /^[A-Za-z0-9_-]{1,64}$/;

/** Newest first, one entry per (machine, project), capped. Pure. */
export function recordRecent(list: RecentProject[], entry: RecentProject): RecentProject[] {
  const rest = list.filter((r) => !(r.machineId === entry.machineId && r.projectId === entry.projectId));
  return [entry, ...rest].slice(0, MAX_RECENT);
}

/**
 * Stored data is untrusted input for URLs: anything that does not look like our ids is dropped. Entries written by an
 * older build used `workspaceId` for what is now `projectId`.
 */
export function parseRecent(raw: string | null): RecentProject[] {
  if (!raw) return [];
  try {
    const data: unknown = JSON.parse(raw);
    if (!Array.isArray(data)) return [];
    const out: RecentProject[] = [];
    for (const item of data as unknown[]) {
      if (typeof item !== "object" || item === null) continue;
      const r = item as Record<string, unknown>;
      const { machineId, name, openedAt, workspaceName, path } = r;
      const projectId = r["projectId"] ?? r["workspaceId"];
      if (
        typeof machineId === "string" &&
        UUID.test(machineId) &&
        typeof projectId === "string" &&
        ID.test(projectId) &&
        typeof name === "string" &&
        typeof openedAt === "number"
      ) {
        out.push({
          machineId,
          projectId,
          name: name.slice(0, 100),
          openedAt,
          ...(typeof workspaceName === "string" ? { workspaceName: workspaceName.slice(0, 100) } : {}),
          ...(typeof path === "string" ? { path: path.slice(0, 200) } : {}),
        });
      }
    }
    return out.slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;
const defaultStore = (): Store | undefined => (typeof localStorage !== "undefined" ? localStorage : undefined);

export function loadRecent(store: Store | undefined = defaultStore()): RecentProject[] {
  try {
    if (!store) return [];
    const current = parseRecent(store.getItem(KEY));
    return current.length > 0 ? current : parseRecent(store.getItem(LEGACY_KEY));
  } catch {
    return [];
  }
}

export function touchRecent(
  entry: Omit<RecentProject, "openedAt">,
  now = Date.now(),
  store: Store | undefined = defaultStore(),
): void {
  try {
    if (!store) return;
    store.setItem(KEY, JSON.stringify(recordRecent(loadRecent(store), { ...entry, openedAt: now })));
    store.removeItem(LEGACY_KEY);
  } catch {
    // storage unavailable: recents are a convenience only
  }
}

export function removeRecent(
  machineId: string,
  projectId: string,
  store: Store | undefined = defaultStore(),
): void {
  try {
    if (!store) return;
    const current = loadRecent(store);
    const updated = current.filter(
      (r) => !(r.machineId === machineId && r.projectId === projectId),
    );
    if (updated.length !== current.length) {
      store.setItem(KEY, JSON.stringify(updated));
      store.removeItem(LEGACY_KEY);
    }
  } catch {
    // storage unavailable: recents are a convenience only
  }
}



export interface RecentView extends RecentProject {
  machineName: string;
  online: boolean;
}

/** Recent entries joined with live machine data; entries of machines that no longer exist are dropped. */
export function viewRecent(recent: RecentProject[], machines: MachineSummary[]): RecentView[] {
  const byId = new Map(machines.map((m) => [m.id, m]));
  return recent.flatMap((r) => {
    const m = byId.get(r.machineId);
    return m ? [{ ...r, machineName: m.name, online: m.online }] : [];
  });
}

/** "Continue" target: the most recently opened Project whose machine still exists. */
export const pickContinue = (views: RecentView[]): RecentView | null => views[0] ?? null;
